import React, { useEffect, useRef, useState } from 'react'
import { useMount } from 'react-use'

import OverlayCard from '@/components/misc/OverlayCard'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { useAuth } from '@/hooks/useAuth'
import { MAX_INTEGER_VALUE } from '@/lib/constants'
import { isOfficeUI } from '@/lib/helpers/officeHelpers'
import authService, { AuthSessionExtended } from '@/lib/services/authService'

const SECONDS_UNTIL_LOGOUT = 120

// Add this component outside SessionProvider so event is properly setup for capture before login
const LogoutWarning: React.FC = () => {
  const [isShowingWarning, setIsShowingWarning] = useState<boolean>(false)
  const [isSSOMaxExpired, setIsSSOMaxExpired] = useState<boolean>(false)
  const [secondsUntilLogout, setSecondsUntilLogout] =
    useState(SECONDS_UNTIL_LOGOUT)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { signOut } = useAuth()

  useMount(() => {
    authService.addEventListener('sessionExtended', (event: Event) => {
      const customEvent = event as CustomEvent<AuthSessionExtended>
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      const secondsUntilLogout = customEvent.detail.secondsRemaining
      if (secondsUntilLogout !== undefined) {
        if (secondsUntilLogout <= 0) {
          // Has already been logged out - perhaps page was left in the background?
          setIsShowingWarning(true)
          setSecondsUntilLogout(0)
          return
        }

        if (secondsUntilLogout <= SECONDS_UNTIL_LOGOUT) {
          console.log('SSO Session Max expired')
          // Normally the session should be extended by SSO Session Idle (5 mins). If it's shorter than SECONDS_UNTIL_LOGOUT it suggests the user may be almost at their SSO Session Max and Keycloak couldn't issue a long enough access token.
          setIsSSOMaxExpired(true)
          return
        }

        timeoutRef.current = setTimeout(
          () => {
            if (isOfficeUI()) {
              // If we're running in Outlook, keep the session alive
              authService.refreshToken()
            } else {
              setIsShowingWarning(true)
              setSecondsUntilLogout(SECONDS_UNTIL_LOGOUT)
            }
          },
          Math.min(
            (secondsUntilLogout - SECONDS_UNTIL_LOGOUT) * 1000,
            MAX_INTEGER_VALUE,
          ),
        )
        return
      }

      return setIsShowingWarning(false)
    })
  })

  useEffect(() => {
    if (isShowingWarning) {
      const interval = setInterval(() => {
        setSecondsUntilLogout((prevSeconds) => prevSeconds - 1)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [isShowingWarning])

  function renderTimeRemaining(seconds: number) {
    if (seconds > 60) {
      const minutes = Math.ceil(seconds / 60)
      return minutes + ' minute' + (minutes > 1 ? 's' : '')
    }

    return seconds + ' seconds'
  }

  function renderContent() {
    if (secondsUntilLogout <= 0 || isSSOMaxExpired) {
      return (
        <>
          <Heading size={2}>You have been logged out</Heading>

          <p className="mb-4">
            You have been logged out{' '}
            {isSSOMaxExpired ? 'for your security' : 'due to inactivity'}.
            <br />
            Refresh or return to the sign in screen.
          </p>

          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              signOut(window.location.href)
            }}
          >
            Login
          </Button>
        </>
      )
    }

    return (
      <>
        <Heading size={2}>You will be logged out soon</Heading>

        <p className="mb-4">
          You will be automatically logged out in{' '}
          {renderTimeRemaining(secondsUntilLogout)} due to inactivity.
        </p>

        <Button
          variant="primary"
          size="lg"
          onClick={async () => {
            await authService.refreshToken()
            setIsShowingWarning(false)
          }}
        >
          Stay logged in
        </Button>
      </>
    )
  }

  if (isShowingWarning || isSSOMaxExpired) {
    return (
      <OverlayCard className="mt-4 sm:mt-18" variant="dark" fullScreen>
        {renderContent()}
      </OverlayCard>
    )
  }

  return null
}

export default LogoutWarning
